import { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bot, MessageCircle, Sparkles, Star, ArrowLeft, Settings,
  Check, X, Play, Pause, Shield, Zap, AlertTriangle,
  ChevronDown, Loader2, RefreshCw, Send, Edit2, ThumbsUp, ThumbsDown,
  Wrench, Flame, Droplet, Building, Home, Paintbrush, Sun, Leaf, TreeDeciduous
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

import { API_URL } from '../../../config/api';

const agentTypes = [
  {
    id: 'community_manager',
    name: 'Community Manager',
    icon: MessageCircle,
    color: 'bg-blue-500',
    description: 'Répond automatiquement aux commentaires sur tes publications',
    features: ['Reponses aux commentaires Facebook', 'Reponses aux commentaires Instagram', 'Detection des questions']
  },
  {
    id: 'content_creator',
    name: 'Content Creator',
    icon: Sparkles,
    color: 'bg-purple-500',
    description: 'Genere des idees de contenu et des publications',
    features: ['Idees de posts', 'Hashtags pertinents', 'Calendrier editorial'],
    comingSoon: true
  },
  {
    id: 'reputation_manager',
    name: 'Reputation Manager',
    icon: Star,
    color: 'bg-amber-500',
    description: 'Gère les avis Google et protège ton e-réputation',
    features: ['Reponses aux avis positifs', 'Gestion des avis negatifs', 'Alertes reputation']
  }
];

const validationModes = [
  { id: 'supervised', name: 'Supervise', icon: Shield, description: 'Toutes les reponses sont validees avant envoi', color: 'text-green-600' },
  { id: 'semi_auto', name: 'Semi-auto', icon: Zap, description: 'Reponses simples auto, complexes validees', color: 'text-amber-600' },
  { id: 'auto', name: 'Automatique', icon: Play, description: 'Tout est envoye automatiquement (deconseille)', color: 'text-red-600' }
];

const businessTypeIcons = {
  plombier: Wrench,
  electricien: Zap,
  macon: Building,
  couvreur: Home,
  menuisier: Home,
  peintre: Paintbrush,
  carreleur: Building,
  etancheur: Droplet,
  terrassier: Building,
  pisciniste: Droplet,
  facadier: Building,
  chauffagiste: Flame,
  jardinier: Leaf,
  paysagiste: TreeDeciduous,
  photovoltaique: Sun,
  multi_corps: Building
};

export default function AIAgentsPage() {
  const { user } = useOutletContext();
  const [agents, setAgents] = useState([]);
  const [settings, setSettings] = useState(null);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [pendingResponses, setPendingResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([
      fetchAgents(),
      fetchSettings(),
      fetchBusinessTypes(),
      fetchPending()
    ]);
    setLoading(false);
  };

  const fetchAgents = async () => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/marketing/agents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setAgents(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchSettings = async () => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/marketing/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setSettings(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchBusinessTypes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/marketing/business-types`);
      if (response.ok) {
        setBusinessTypes(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchPending = async () => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/marketing/agents/pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setPendingResponses(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const toggleAgent = async (agentType, currentState) => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/marketing/agents/${agentType}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !currentState })
      });

      if (response.ok) {
        toast.success(!currentState ? 'Agent active' : 'Agent desactive');
        fetchAgents();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const updateAgentValidationMode = async (agentType, mode) => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/marketing/agents/${agentType}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ validation_mode: mode })
      });

      if (response.ok) {
        toast.success('Mode de validation mis a jour');
        fetchAgents();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const updateSettings = async (field, value) => {
    const token = localStorage.getItem('client_token');
    try {
      const params = new URLSearchParams({ [field]: value });
      const response = await fetch(`${API_URL}/api/marketing/settings?${params}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Parametres mis a jour');
        fetchSettings();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const generateBulkResponses = async () => {
    const token = localStorage.getItem('client_token');
    setGenerating(true);

    try {
      const response = await fetch(`${API_URL}/api/marketing/agents/generate-bulk`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchPending();
      } else {
        toast.error('Erreur lors de la generation');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setGenerating(false);
    }
  };

  const approvePending = async (pendingId, editedContent = null) => {
    const token = localStorage.getItem('client_token');
    try {
      const params = editedContent ? new URLSearchParams({ edited_content: editedContent }) : '';
      const response = await fetch(`${API_URL}/api/marketing/agents/pending/${pendingId}/approve?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Reponse envoyee');
        fetchPending();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const rejectPending = async (pendingId) => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/marketing/agents/pending/${pendingId}/reject`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Reponse rejetee');
        fetchPending();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const getAgentConfig = (type) => {
    return agents.find(a => a.agent_type === type) || {};
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-rm-primary" />
      </div>
    );
  }

  const activeAgents = agents.filter(a => a.is_active).length;
  const BusinessIcon = settings?.business_type ? (businessTypeIcons[settings.business_type] || Building) : Building;

  return (
    <div className="space-y-6" data-testid="ai-agents-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/espace-client/marketing">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-xl font-bold text-rm-text flex items-center gap-2">
              <Bot className="w-6 h-6" />
              Agents IA
            </h1>
            <p className="text-sm text-slate-500">Automatise tes réponses avec l'intelligence artificielle</p>
          </div>
        </div>
        <Button variant="outline" onClick={() => setShowSettings(!showSettings)}>
          <Settings className="w-4 h-4 mr-2" />
          Parametres
        </Button>
      </div>

      {/* Business Profile Card */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-white rounded-xl border border-slate-200 overflow-hidden"
          >
            <div className="p-6">
              <h3 className="font-semibold text-rm-text mb-4 flex items-center gap-2">
                <BusinessIcon className="w-5 h-5" />
                Profil de ton entreprise
              </h3>
              <p className="text-sm text-slate-500 mb-4">
                Ces informations permettent aux agents IA de personnaliser leurs reponses
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Type de metier</label>
                  <select
                    value={settings?.business_type || ''}
                    onChange={(e) => updateSettings('business_type', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  >
                    <option value="">Sélectionne ton métier</option>
                    {businessTypes.map(bt => (
                      <option key={bt.id} value={bt.id}>{bt.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nom de l'entreprise</label>
                  <input
                    type="text"
                    value={settings?.business_name || ''}
                    onChange={(e) => updateSettings('business_name', e.target.value)}
                    placeholder="Ex: Plomberie Martin"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Ville / Region</label>
                  <input
                    type="text"
                    value={settings?.business_city || ''}
                    onChange={(e) => updateSettings('business_city', e.target.value)}
                    placeholder="Ex: Lyon et environs"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Telephone</label>
                  <input
                    type="text"
                    value={settings?.business_phone || ''}
                    onChange={(e) => updateSettings('business_phone', e.target.value)}
                    placeholder="Ex: 04 XX XX XX XX"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Agents actifs</p>
          <p className="text-2xl font-bold text-rm-text">{activeAgents} / 3</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">En attente de validation</p>
          <p className="text-2xl font-bold text-amber-600">{pendingResponses.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Reponses generees</p>
          <p className="text-2xl font-bold text-rm-text">
            {agents.reduce((sum, a) => sum + (a.total_generated || 0), 0)}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Taux d'approbation</p>
          <p className="text-2xl font-bold text-green-600">
            {(() => {
              const total = agents.reduce((sum, a) => sum + (a.total_approved || 0) + (a.total_rejected || 0), 0);
              const approved = agents.reduce((sum, a) => sum + (a.total_approved || 0), 0);
              return total > 0 ? Math.round((approved / total) * 100) : 0;
            })()}%
          </p>
        </div>
      </div>

      {/* Agents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {agentTypes.map(agent => {
          const config = getAgentConfig(agent.id);
          const Icon = agent.icon;
          const isActive = config.is_active;
          const mode = validationModes.find(m => m.id === config.validation_mode) || validationModes[0];
          const ModeIcon = mode.icon;

          return (
            <motion.div
              key={agent.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-white rounded-xl border-2 overflow-hidden transition-all ${
                isActive ? 'border-green-300' : 'border-slate-200'
              } ${agent.comingSoon ? 'opacity-60' : ''}`}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white ${agent.color}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-rm-text">{agent.name}</h3>
                      {isActive ? (
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <Check className="w-3 h-3" /> Actif
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">Inactif</span>
                      )}
                    </div>
                  </div>
                  
                  {!agent.comingSoon && (
                    <button
                      onClick={() => toggleAgent(agent.id, isActive)}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        isActive ? 'bg-green-500' : 'bg-slate-300'
                      }`}
                    >
                      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        isActive ? 'left-7' : 'left-1'
                      }`} />
                    </button>
                  )}
                </div>

                <p className="text-sm text-slate-600 mb-4">{agent.description}</p>

                <div className="space-y-2 mb-4">
                  {agent.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-slate-500">
                      <Check className="w-4 h-4 text-green-500" />
                      {feature}
                    </div>
                  ))}
                </div>

                {agent.comingSoon ? (
                  <div className="bg-slate-100 rounded-lg p-3 text-center">
                    <p className="text-sm text-slate-500">Bientot disponible</p>
                  </div>
                ) : (
                  <>
                    {/* Validation Mode */}
                    <div className="border-t border-slate-100 pt-4">
                      <label className="block text-xs text-slate-500 mb-2">Mode de validation</label>
                      <div className="flex gap-2">
                        {validationModes.map(m => {
                          const MIcon = m.icon;
                          return (
                            <button
                              key={m.id}
                              onClick={() => updateAgentValidationMode(agent.id, m.id)}
                              className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs transition-colors ${
                                config.validation_mode === m.id
                                  ? 'bg-rm-primary text-white'
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              }`}
                              title={m.description}
                            >
                              <MIcon className="w-3 h-3" />
                              {m.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex justify-between mt-4 pt-4 border-t border-slate-100 text-xs text-slate-500">
                      <span>Genere: {config.total_generated || 0}</span>
                      <span className="text-green-600">Approuve: {config.total_approved || 0}</span>
                      <span className="text-red-600">Rejete: {config.total_rejected || 0}</span>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Pending Responses */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-rm-text">Reponses en attente de validation</h3>
            <p className="text-sm text-slate-500">Validez ou rejetez les reponses generees par l'IA</p>
          </div>
          <Button onClick={generateBulkResponses} disabled={generating}>
            {generating ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            Generer des reponses
          </Button>
        </div>

        {pendingResponses.length === 0 ? (
          <div className="p-8 text-center">
            <Bot className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Aucune reponse en attente</p>
            <p className="text-sm text-slate-400 mt-1">
              Activez un agent et cliquez sur "Generer des reponses" pour commencer
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
            {pendingResponses.map(pending => (
              <PendingResponseRow
                key={pending.id}
                pending={pending}
                onApprove={approvePending}
                onReject={rejectPending}
              />
            ))}
          </div>
        )}
      </div>

      {/* Warning Banner */}
      {!settings?.business_type && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">Configuration incomplete</p>
            <p className="text-sm text-amber-600">
              Sélectionne ton type de métier dans les paramètres pour que les agents IA génèrent des réponses adaptées à ton activité.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => setShowSettings(true)}
            >
              Configurer maintenant
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Pending Response Row Component
function PendingResponseRow({ pending, onApprove, onReject }) {
  const [editing, setEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(pending.generated_content);
  const [approving, setApproving] = useState(false);

  const handleApprove = async () => {
    setApproving(true);
    await onApprove(pending.id, editing ? editedContent : null);
    setApproving(false);
  };

  const agentInfo = agentTypes.find(a => a.id === pending.agent_type) || {};
  const Icon = agentInfo.icon || Bot;

  return (
    <div className="p-4">
      <div className="flex items-start gap-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white flex-shrink-0 ${agentInfo.color || 'bg-slate-500'}`}>
          <Icon className="w-5 h-5" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-medium text-rm-text">{pending.source_author}</span>
            <span className="text-xs text-slate-400">
              {pending.source_type === 'comment' ? 'Commentaire' :
               pending.source_type === 'review' ? 'Avis Google' : 'Message'}
            </span>
            <span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full text-slate-500">
              {pending.source_platform}
            </span>
          </div>
          
          {/* Original content */}
          <div className="bg-slate-50 rounded-lg p-3 mb-3">
            <p className="text-sm text-slate-600">{pending.source_content}</p>
          </div>
          
          {/* Generated response */}
          <div className="bg-blue-50 rounded-lg p-3 border-l-4 border-blue-500">
            <p className="text-xs text-blue-600 mb-1 flex items-center gap-1">
              <Bot className="w-3 h-3" />
              Reponse generee par l'IA
            </p>
            {editing ? (
              <textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                rows={3}
                className="w-full px-2 py-1 border border-blue-200 rounded text-sm resize-none"
              />
            ) : (
              <p className="text-slate-700">{pending.generated_content}</p>
            )}
          </div>
          
          {/* Actions */}
          <div className="flex items-center justify-between mt-3">
            <button
              onClick={() => setEditing(!editing)}
              className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
            >
              <Edit2 className="w-3 h-3" />
              {editing ? 'Annuler' : 'Modifier'}
            </button>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onReject(pending.id)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <ThumbsDown className="w-4 h-4 mr-1" />
                Rejeter
              </Button>
              <Button
                size="sm"
                onClick={handleApprove}
                disabled={approving}
              >
                {approving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                ) : (
                  <ThumbsUp className="w-4 h-4 mr-1" />
                )}
                Approuver et envoyer
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
